import { CheckoutSummary } from './checkout-summary'
import { ERROR_CODES, CheckoutCriticalError, displayAndLogCriticalError } from './checkout-critical-errors'
import { CheckoutExistingOrders } from './checkout-existing-orders'
import { parseAddressByFields, fieldsForCountry } from '../Utils/checkout-address'
import { AddressFields } from '../types'
import { CheckoutDigitalWallet } from './checkout-digital-wallet'
import { computed } from 'nanostores'
import { CheckoutCart } from 'src/Elements/Checkout/V2/types'
import { RebillyError } from 'javascript/lander/rebilly_element'
import { CFFetch } from 'javascript/lander/utils/fetcher'

const INITIALIAZE_PAI_TIMEOUT = 60000
const FRAMEPAY_SCRIPT_URL = 'https://framepay.payments.ai/rebilly.js'
const FRAMEPAY_CSS_URL = 'https://framepay.payments.ai/rebilly.css'
export class TimeoutError extends Error {}

declare global {
  interface Window {
    turnstileSiteKey: string
    turnstile: {
      render: (selector: string, options: { sitekey: string; callback: (token: string) => void }) => void
    }
  }
}

const buildPAIContext = (extra: Record<string, unknown> = {}): Record<string, unknown> => ({
  phase: 'pai-init',
  visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
  pageUrl: typeof location !== 'undefined' ? location.href : null,
  rebillyLoaded: !!globalThis.Rebilly,
  rebillyInitialized: !!globalThis.Rebilly?.initialized,
  ...extra,
})

const buildPAIError = (
  code: string,
  cause: Error,
  extraContext: Record<string, unknown> = {}
): CheckoutCriticalError => {
  const error = new CheckoutCriticalError(code, { cause })
  error.context = buildPAIContext(extraContext)
  return error
}

const extractRebillyErrorFields = (
  e: unknown
): { rebillyErrorCode: string | null; rebillyErrorMessage: string | null } => ({
  rebillyErrorCode:
    (e as { code?: string; data?: { code?: string } })?.code ?? (e as { data?: { code?: string } })?.data?.code ?? null,
  rebillyErrorMessage:
    (e as { data?: { message?: string } })?.data?.message ?? (e as { message?: string })?.message ?? null,
})

export { extractRebillyErrorFields }

export const loadPAIDeps = (addLoader = true): Promise<void> | undefined => {
  if (globalThis.Rebilly?.initialized) return Promise.resolve()
  if (globalThis.Checkout.store.payment.state.get() != globalThis.Checkout.PaymentStates.START) return undefined

  const startedAt = performance.now()

  globalThis.Checkout.store.payment.state.set(globalThis.Checkout.PaymentStates.INITIALIZING)
  if (addLoader) globalThis.Checkout.store.payment.state.set(globalThis.Checkout.PaymentStates.LOADING)
  const js = document.createElement('script') as HTMLScriptElement
  js.src = FRAMEPAY_SCRIPT_URL

  const onFailure = () => {
    resetPAIConfigState()
    globalThis.Checkout.store.payment.state.set(globalThis.Checkout.PaymentStates.START)
  }

  const promise = new Promise<void>((resolve, reject) => {
    js.onload = () => {
      const scriptLoadMs = Math.round(performance.now() - startedAt)
      intializePAI()
        .then(() => {
          const summary = globalThis.Checkout.store.summary.get()
          const cart = globalThis.Checkout.computed.checkoutCart.get() as CheckoutCart
          // Always run the post-init update, even when isUpdatingRebilly is set: the flag
          // starts true and previews that completed before init were dropped on
          // !RebillyFullyInitialized, so nothing is actually in flight to complete it —
          // skipping here left Rebilly on the placeholder transaction data and the Apple
          // Pay button spinning forever (ENG-6708 / ENG-6871). No forceRebillyUpdate needed:
          // on first init #lastRebillyUpdate is still unset (pre-init previews return before
          // it is assigned), so the dedupe guard cannot match anyway; forcing would only add
          // a duplicate Rebilly.update if loadPAIDeps re-runs after onFailure().
          // Prefer the live store summary, but fall back to the last previewed summary: on a
          // wallet checkout with invalid/absent billing the store is never written
          // (shouldUpdateSummaryData is false) so summary.data has no line items even though a
          // preview computed the real amount. Using the cached data lets this update push the
          // real amount, closing the amount:1 "ready" window without inferring preview state.
          // Return the inner promise so a READY_TO_PAY_UPDATE_ERROR rejection
          // bubbles into the .catch below instead of being silently dropped.
          const summaryData = summary.data?.line_items?.length ? summary.data : CheckoutSummary.lastPreviewedSummary
          return CheckoutSummary.updateRebillyTransactionDataPromise(summaryData, cart).then(() => {
            resolve()
          })
        })
        .catch((e) => {
          // Snapshot retries before onFailure() resets the counter so we keep
          // the diagnostically-valuable retry count in the Honeybadger context.
          const retries = initializePAIConfigRetries
          // Only run PAI-init recovery (resetting payment.state to START and
          // clearing the cached init promise) for true PAI-init failures.
          // A post-init failure such as READY_TO_PAY_UPDATE_ERROR happens
          // AFTER Rebilly fired `ready` and set globalThis.Rebilly.initialized
          // = true — resetting state here would permanently desync our store
          // from Rebilly for the session (every subsequent loadPAIDeps()
          // short-circuits on Rebilly.initialized, and every subsequent
          // updateRebillyTransactionDataPromise short-circuits on
          // !RebillyFullyInitialized).
          const isPaiInitFailure = !(e instanceof CheckoutCriticalError) || e.context?.phase === 'pai-init'
          if (isPaiInitFailure) {
            onFailure()
          }
          const elapsedMs = Math.round(performance.now() - startedAt)
          // If a downstream step already produced a CheckoutCriticalError
          // (e.g. PAI_INITIALIZATION_CONFIG_ERROR from intializePAI, or a
          // READY_TO_PAY_UPDATE_ERROR from updateRebillyTransactionDataPromise),
          // re-raise it as-is. Only merge PAI-init timing fields when the
          // existing context describes a PAI-init phase — otherwise they'd
          // mislabel timing on errors from a different phase.
          if (e instanceof CheckoutCriticalError) {
            if (e.context?.phase === 'pai-init') {
              e.context = { ...e.context, elapsedMs, scriptLoadMs, retries }
            }
            reject(e)
            return
          }
          const errorType =
            e instanceof TimeoutError
              ? ERROR_CODES.PAI_INITIALIZATION_TIMEOUT_ERROR
              : ERROR_CODES.UNEXPECTED_INITIALIZATION_ERROR
          reject(
            buildPAIError(errorType, e, {
              elapsedMs,
              scriptLoadMs,
              retries,
              ...extractRebillyErrorFields(e),
            })
          )
        })
    }
    js.onerror = () => {
      const retries = initializePAIConfigRetries
      const elapsedMs = Math.round(performance.now() - startedAt)
      onFailure()
      reject(
        buildPAIError(ERROR_CODES.PAI_INITIALIZATION_SCRIPT_ERROR, new Error('framepay JS onError load'), {
          elapsedMs,
          retries,
          scriptSrc: FRAMEPAY_SCRIPT_URL,
        })
      )
    }
  })
  document.head.appendChild(js)

  const css = document.createElement('link')
  css.href = FRAMEPAY_CSS_URL
  css.rel = 'stylesheet'
  document.head.appendChild(css)

  return promise
}

// Reset cached PAI promise & retry counter so a re-attempt can start fresh after a failure.
const resetPAIConfigState = (): void => {
  initializePAIConfigPromise = undefined
  if (initializePAIConfigNetworkTimeout) {
    clearTimeout(initializePAIConfigNetworkTimeout)
    initializePAIConfigNetworkTimeout = undefined
  }
  initializePAIConfigRetries = 0
}

const initializeTurnstile = (): void => {
  const turnstilejs = document.createElement('script') as HTMLScriptElement
  turnstilejs.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

  turnstilejs.onload = () => {
    const key = window.turnstileSiteKey
    window.turnstile.render('#cf-turnstile', {
      sitekey: key,
      callback: function (token: string) {
        globalThis.Checkout.turnstileToken = token
      },
    })
  }

  turnstilejs.onerror = (e) => {
    console.error('Turnstile error', e)
  }

  document.head.appendChild(turnstilejs)
}

function toPascalCase(kebabCase: string): string {
  return kebabCase.replace(/(^\w|-\w)/g, (text) => {
    return text.replace(/-/, '').toUpperCase()
  })
}

const rebillyErrorClasses = {}
let hasGeneratedRebillyErrorClasses
function generateRebillyErrorClasses() {
  if (hasGeneratedRebillyErrorClasses) return
  hasGeneratedRebillyErrorClasses = true
  const errorGenerator = (name) =>
    ({
      [name]: class extends Error {
        data: any
        constructor(data) {
          super()
          this.data = data
        }
      },
    }[name])
  Object.values(globalThis.Rebilly.errorCodes).forEach((errorCode: string) => {
    const errorClassName = toPascalCase(errorCode)
    rebillyErrorClasses[errorClassName] = errorGenerator(errorClassName)
  })
}

let initializePAIConfigPromise
let initializePAIConfigNetworkTimeout
let initializePAIConfigRetries = 0
const initializePAIConfig = (rebillyConfig): Promise<void> => {
  if (initializePAIConfigPromise) return initializePAIConfigPromise
  initializePAIConfigPromise = new Promise<void>((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | null = null
    const armTimeout = () => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => {
        reject(new TimeoutError(`Timeout error: Rebilly took more than ${INITIALIAZE_PAI_TIMEOUT}ms to load`))
      }, INITIALIAZE_PAI_TIMEOUT)
    }
    const cancelTimeout = () => {
      if (timeout) clearTimeout(timeout)
      timeout = null
    }

    armTimeout()

    globalThis.Rebilly.on('ready', () => {
      if (initializePAIConfigNetworkTimeout) clearTimeout(initializePAIConfigNetworkTimeout)
      cancelTimeout()
      try {
        globalThis.Checkout.store.payment.state.set(globalThis.Checkout.PaymentStates.INITIALIZED)
        resolve()
      } catch (e) {
        reject(e)
      }
    })

    globalThis.Rebilly.on('error', (rebillyError: RebillyError) => {
      generateRebillyErrorClasses()
      const errorClassName =
        rebillyError.code && typeof rebillyError.code == 'string' ? toPascalCase(rebillyError.code) : null

      if (!errorClassName || !rebillyErrorClasses[errorClassName]) {
        cancelTimeout()
        reject(rebillyError)
        return
      }

      const error = new rebillyErrorClasses[errorClassName](rebillyError)
      if (rebillyError.code == 'network-error') {
        if (initializePAIConfigRetries < 5) {
          // Reset the outer timeout so each retry has a fresh budget instead of
          // sharing the original 60s window with all previous retries.
          armTimeout()
          initializePAIConfigNetworkTimeout = setTimeout(() => {
            globalThis.Rebilly.initialize(rebillyConfig)
            initializePAIConfigRetries++
          }, 2000 * initializePAIConfigRetries)
        } else {
          cancelTimeout()
          reject(error)
        }
      } else if (rebillyError.code == 'already-initialized') {
        cancelTimeout()
        resolve()
      } else {
        cancelTimeout()
        reject(error)
      }
    })
  })
  globalThis.Rebilly.initialize(rebillyConfig)
  return initializePAIConfigPromise
}

const intializePAI = (): Promise<void> => {
  globalThis.Checkout.PaypalCallbacks = {
    onInit: () => {
      console.log('[PayPal] - onInit called')
      globalThis.Checkout.store.payment.paypal.state.set({ state: globalThis.Checkout.PaypalStates.INITIALIZED })
    },
    onApprove: (data, actions) => {
      console.log('[PayPal] - onApprove called', data, actions)
      globalThis.Checkout.store.payment.paypal.state.set({
        state: globalThis.Checkout.PaypalStates.PAYMENT_METHOD_APPROVED,
      })
    },
    onError: (data, actions) => {
      console.log('[PayPal] - onError called', data, actions)

      globalThis.Checkout.store.payment.paypal.state.set({
        state: globalThis.Checkout.PaypalStates.ERROR,
        code: globalThis.Checkout.ErrorTypes.PAYPAL_CUSTOM_ERROR,
        message: 'Something unexpected happened',
      })
    },
    onCancel: (data, actions) => {
      console.log('[PayPal] - onCancel called', data, actions)

      globalThis.Checkout.store.payment.paypal.state.set({
        state: globalThis.Checkout.PaypalStates.ERROR,
        code: globalThis.Checkout.ErrorTypes.PAYPAL_DECLINED_ERROR,
      })
    },
    onClick: (data, actions) => {
      console.log('[PayPal] - onClick called', data, actions)
      globalThis.Checkout.store.payment.paypal.state.set({
        state: globalThis.Checkout.PaypalStates.ADDING_PAYMENT_METHOD,
      })
    },
  }
  const checkoutElement = document.querySelector('[data-page-element="Checkout/V2"]')
  const checkoutComponent = (checkoutElement as any).cf2_instance
  const inputColor = getComputedStyle(checkoutElement).getPropertyValue('--input-color')
  const fontFamily = getComputedStyle(checkoutElement).getPropertyValue('--multiple-payments-font-family')
  const fontSize = getComputedStyle(checkoutElement).getPropertyValue('--multiple-payments-font-size')
  const rebillyKeys = document.getElementById('payment-gateway-keys')
  if (!rebillyKeys) {
    return Promise.reject(
      buildPAIError(ERROR_CODES.PAI_INITIALIZATION_CONFIG_ERROR, new Error('payment-gateway-keys element not found'))
    )
  }
  const publishableKey = rebillyKeys.getAttribute('data-rebilly-publishable-key')
  const organizationId = rebillyKeys.getAttribute('data-rebilly-organization-id')
  const websiteId = rebillyKeys.getAttribute('data-rebilly-website-id')
  const currency = rebillyKeys.getAttribute('data-rebilly-currency')
  if (!publishableKey) {
    return Promise.reject(
      buildPAIError(ERROR_CODES.PAI_INITIALIZATION_CONFIG_ERROR, new Error('Missing required Rebilly publishable key'), {
        hasPublishableKey: !!publishableKey,
        hasOrganizationId: !!organizationId,
        hasWebsiteId: !!websiteId,
      })
    )
  }
  const placeholders = checkoutComponent.cardPaymentPlaceholders
  const rebillyConfig = {
    publishableKey: publishableKey,
    organizationId: organizationId,
    kountAccountId: '700000', // This is for capturing kount fraud sessions
    websiteId: websiteId,
    // NOTE: need to add below otherwise this error happens: transactionData must contain an amount to fetch methods
    transactionData: {
      currency: currency || 'USD',
      amount: 1,
      label: 'Product Purchase',
    },
    icon: {
      color: inputColor,
    },
    style: {
      base: {
        color: inputColor,
        fontFamily: fontFamily,
        fontSize: fontSize,
        '::placeholder': {
          color: inputColor,
          fontFamily: fontFamily,
          fontSize: fontSize,
        },
      },
    },
    placeholders: {
      card: {
        number: placeholders.number,
        ...(placeholders.expiration.includes('/')
          ? {
              expirationSeparator: '/',
              expirationMonth: placeholders.expiration.split('/')[0],
              expirationYear: placeholders.expiration.split('/')[1],
            }
          : {
              expiration: placeholders.expiration,
            }),
        cvv: placeholders.cvv,
      },
    },
  }

  CheckoutDigitalWallet.initialize()
  return initializePAIConfig(rebillyConfig)
}

type Options = {
  hasPhoneNumber: boolean
  billingFields: AddressFields
  shippingFields: AddressFields
}

const initializeCountries = (options: Options): Promise<void> => {
  return globalThis
    .CFFetch(
      '/cf_countries_states.json',
      {
        headers: { 'Content-Type': 'application/json' },
      },
      { retries: 3, shouldCaptureServerError: true }
    )
    .then(function (res) {
      return res.json()
    })
    .catch((e) => {
      throw new CheckoutCriticalError(ERROR_CODES.FETCH_COUNTRIES_STATES_ERROR, {
        cause: e,
      })
    })
    .then((countryStateReqResponse) => {
      globalThis.Checkout.allCountries = countryStateReqResponse.result

      const defaultCountryCode = 'US'

      const countryData =
        globalThis.Checkout.allCountries.find((c) => c.iso2 == globalThis.cfVisitorData.country) ??
        globalThis.Checkout.allCountries.find((c) => c.iso2 == defaultCountryCode)

      const stateData =
        countryData.regions?.find((r) => r.iso2 == globalThis.cfVisitorData.regionCode) ?? countryData.regions?.[0]

      // TODO: check if this works for default country code case
      const country = countryData.iso2
      const state = stateData?.state_code

      globalThis.Checkout.contactLocale = {
        country: country,
        state: state,
      }

      const initialShipping = globalThis.Checkout.store.shipping.get()
      globalThis.Checkout.store.shipping.set({
        ...(initialShipping ?? {}),
        country: initialShipping?.country ?? country,
        state: initialShipping?.state ?? state,
      })

      let initialBilling = globalThis.Checkout.store.billing.get()
      if (globalThis.Checkout.store.checkout.mode.get() == 'guest') {
        initialBilling = parseAddressByFields(initialBilling, options.billingFields)
      }
      globalThis.Checkout.store.billing.set(initialBilling)

      globalThis.Checkout.store.phoneNumberInitialized.set(true)
    })
}

const checkFeatureFlags = (): Promise<void> => {
  return CFFetch('/user_pages/api/v1/checkouts/feature_flags.json', {}, { retries: 3, shouldCaptureServerError: true })
    .then((res) => {
      return res.json()
    })
    .then((res) => {
      globalThis.Checkout.store.featureFlags.isShippingEnabled.set(res.feature_flags.is_shipping_enabled)
    })
    .catch((e) => {
      throw new CheckoutCriticalError(ERROR_CODES.FETCH_FEATURE_FLAGS_ERROR, {
        cause: e,
      })
    })
}

export const initializeMachine = (options: Options): void => {
  globalThis.Checkout.store.state.listen((newState) => {
    switch (newState) {
      case globalThis.Checkout.StoreStates.INITIALIZING: {
        const isGuest = globalThis.Checkout.store.checkout.mode.get() == 'guest'
        const isSaved = globalThis.Checkout.store.checkout.mode.get() == 'saved'
        const isOTO = globalThis.Checkout.store.checkout.mode.get() == 'oto'
        const paymentMethods = globalThis.Checkout.store.paymentMethods.get()
        const oneStepCheckout = document.querySelector('[data-page-element="Checkout/V2"][data-total-steps="1"]')
        const expressPaymentAvailable =
          globalThis.globalResourceData.expressEnabledPayments &&
          globalThis.globalResourceData.expressEnabledPayments.length > 0
        const shouldIntializePAI =
          (oneStepCheckout && isGuest) ||
          (!paymentMethods.length && (isSaved || isOTO)) ||
          (expressPaymentAvailable && isSaved)
        const shouldInitializeTurnstile = window.turnstileSiteKey && window.turnstileSiteKey.length

        if (shouldInitializeTurnstile) initializeTurnstile()

        Promise.all(
          [
            checkFeatureFlags(),
            shouldIntializePAI && loadPAIDeps(false),
            initializeCountries(options),
            !isGuest && Object.keys(globalThis.Checkout.productsById).length > 0 && CheckoutExistingOrders.fetch(),
          ].filter((v) => !!v)
        )
          .then(() => {
            globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.INITIALIZED)
          })
          .catch((e) => {
            if (e instanceof CheckoutCriticalError) {
              displayAndLogCriticalError(e)
            } else {
              const initializationError = new CheckoutCriticalError(ERROR_CODES.UNEXPECTED_INITIALIZATION_ERROR, {
                cause: e,
              })
              displayAndLogCriticalError(initializationError)
            }
          })

        break
      }
      case globalThis.Checkout.StoreStates.INITIALIZED: {
        CheckoutSummary.sendOrderPreview()
        // NOTE: Change mode to filling form after all callbacks
        // for Initialized have been executed.
        setTimeout(() => {
          globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.FILLING_FORM)
        })
        break
      }
      case globalThis.Checkout.StoreStates.FILLING_FORM: {
        break
      }
      case globalThis.Checkout.StoreStates.SUBMITTED: {
        break
      }
    }
  })
  // # endregion state machine listeners

  // # region global listeners
  globalThis.Checkout.store.submitting.listen((submitting) => {
    const state = submitting.state
    switch (state) {
      case globalThis.Checkout.SubmittingStates.ERROR: {
        globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.FILLING_FORM)
        break
      }
      case globalThis.Checkout.SubmittingStates.DONE: {
        globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.SUBMITTED)
        break
      }
    }
  })

  globalThis.Checkout.store.billing.listen((billing) => {
    const fields = globalThis.Checkout.store.billingFields.get()
    const addressFields = fieldsForCountry(fields, billing.country)
    globalThis.Checkout.store.billingFields.set(addressFields)
  })

  globalThis.Checkout.store.payment.paypal.state.listen((submitting) => {
    const state = submitting.state
    switch (state) {
      case globalThis.Checkout.PaypalStates.ERROR: {
        setTimeout(() => {
          globalThis.Checkout.store.payment.paypal.state.set({ state: globalThis.Checkout.PaypalStates.INITIALIZED })
        }, 3000)
        break
      }
    }
  })

  globalThis.Checkout.store.checkout.mode.listen((mode) => {
    if (mode === 'guest') {
      const cart = globalThis.Checkout.computed.checkoutCart.get() as CheckoutCart
      CheckoutExistingOrders.clearUpdatableOrders(cart.items[0]?.product_id)

      // #region reset contact data
      globalThis.Checkout.store.contact.set({})
      globalThis.Checkout.store.shipping.set({
        country: globalThis.Checkout.contactLocale.country,
        state: globalThis.Checkout.contactLocale.state,
      })

      const newBillingData = parseAddressByFields(globalThis.Checkout.contactLocale, options.billingFields)
      globalThis.Checkout.store.billing.set(newBillingData)
      globalThis.Checkout.store.payment.id.set(null)
      // #endregion reset contact data
    }
  })

  // Adds PAI when the user clicks to add a new payment
  globalThis.Checkout.store.payment.id.listen((newPaymentId) => {
    if (!newPaymentId) loadPAIDeps()?.catch((e) => displayAndLogCriticalError(e))
  })
  globalThis.Checkout.computed.isOTORetrialPayment.listen((isOTORetrialPayment) => {
    if (isOTORetrialPayment) loadPAIDeps()?.catch((e) => displayAndLogCriticalError(e))
  })

  const checkoutStatesChangedByCartItems = {
    [globalThis.Checkout.CheckoutStates.UPGRADE_DOWNGRADE]: true,
    [globalThis.Checkout.CheckoutStates.REACTIVATE]: true,
  }

  computed([globalThis.Checkout.computed.modeLeaveEnterEvent, globalThis.Checkout.store.state], (ev, state) => {
    if ([globalThis.Checkout.StoreStates.START, globalThis.Checkout.StoreStates.INITIALIZING].includes(state)) return
    const { enter } = ev
    if (enter && !checkoutStatesChangedByCartItems[enter]) {
      globalThis.Checkout.store.checkout.lastModeIndependentOfCartItems.set(enter)
    }

    // Add PAI when entering on guest mode and page has a one step checkout
    // Or when entering on saved mode and the contact does not have any saved payments
    const oneStepCheckout = document.querySelector('[data-page-element="Checkout/V2"][data-total-steps="1"]')
    if (enter === 'guest' && oneStepCheckout) {
      loadPAIDeps()?.catch((e) => displayAndLogCriticalError(e))
    }
    if (enter === 'saved') {
      const contactPaymentMethod = globalThis.Checkout.store.paymentMethods.get()
      if (!contactPaymentMethod?.length) {
        loadPAIDeps()?.catch((e) => displayAndLogCriticalError(e))
      }
    }
  }).subscribe(() => {})

  globalThis.Checkout.computed.checkoutCart.listen((cart) => {
    const currentMode = globalThis.Checkout.store.checkout.mode.get()

    let cartState
    cart.items.forEach(({ type }) => {
      if (type) {
        if (cartState) {
          throw new Error(`Cart has more than one item of type ${type}`)
        }
        cartState = type
      }
    })

    if (cartState && cartState != currentMode) {
      globalThis.Checkout.store.checkout.mode.set(cartState)
    } else if (
      !cartState &&
      [globalThis.Checkout.CheckoutStates.UPGRADE_DOWNGRADE, globalThis.Checkout.CheckoutStates.REACTIVATE].includes(
        currentMode
      )
    ) {
      const lastModeIndependentOfCartItems = globalThis.Checkout.store.checkout.lastModeIndependentOfCartItems.get()
      if (lastModeIndependentOfCartItems && lastModeIndependentOfCartItems != currentMode) {
        globalThis.Checkout.store.checkout.mode.set(lastModeIndependentOfCartItems)
      }
    }
  })

  let lastBilling
  globalThis.Checkout.store.billing.listen((billing) => {
    if (globalThis.Checkout.store.billingApiErrorsByField.get()) globalThis.Checkout.store.billingApiErrorsByField.set()
    const backfilledBillingFromApi = options.billingFields.length == 2 && Object.keys(billing).length > 2
    const updatedZipForSameAddress = !billing.id && lastBilling?.zip != billing.zip
    // NOTE: If zip code changes for a new address, cleans up other fields calculated by API
    // in order to recalculate them by back-end again, otherwise, we would be storing wrong data here
    if (backfilledBillingFromApi && updatedZipForSameAddress) {
      const newBillingAddress = {
        country: billing.country,
        zip: billing.zip,
      }
      globalThis.Checkout.store.billing.set(newBillingAddress)
    }
    lastBilling = billing
  })
  // # endregion global listeners
}
